import { Component, OnInit, WritableSignal, computed, inject, input, signal } from '@angular/core';

import { AgGridModule } from 'ag-grid-angular';
import { GetRowIdFunc, GridReadyEvent, ModelUpdatedEvent } from 'ag-grid-community';
import { Column, DomLayoutType, GridReadyEvent as PGridReadyEvent, RowModelType } from 'ag-grid-enterprise';
import {
  EMPTY,
  MonoTypeOperatorFunction,
  Observable,
  Subject,
  catchError,
  concatMap,
  debounceTime,
  defer,
  finalize,
  map,
  switchMap,
  tap,
} from 'rxjs';

import { ENotificationCode, LAZY_SERVICE_TOKEN } from '@camelot/notification';
import { Logger } from '@camelot/server';

import {
  ContentMenuAction,
  ContentMenuActionType,
  EditabledCellEdit,
  EditabledShortcutEvent,
  GridMutationResult,
  IContentMenuActionConfig,
  IEditabledDataService,
  IEditabledDataUpdateFields,
} from '../../../models/types';
import { EditabledGridRow } from '../../../services/dto/grid-row';
import { InsertPosition } from '../../../services/dto/insert-position';
import { GridMetaData } from '../../../services/dto/metadata';
import { RowType } from '../../../services/dto/row-type';
import { CamTranslationGrid } from '../../../translation.service';
import { BaseGridComponent } from '../../base-grid.component';
import { GridCommentRowComponent } from '../comment-row/comment-row.component';
import {
  ADD_ROW_ACTIONS,
  ALL_ROW_TYPES,
  EditabledMenuContext,
  buildSelectionMenuItems,
  createEditabledContextMenu,
} from './context-menu';
import { EditabledCtaRowComponent } from './cta-row/editabled-cta-row.component';
import { DEFAULT_EDITABLED_CONFIG, EDITABLED_CONFIG, IEditabledConfig } from './provider';
import { EditabledSelectedRowComponent } from './selected-row/editabled-selected-row.component';

const DEFAULT_COLUMN_WIDTH = 200;
const PINNED_VIEWPORT_MARGIN = 80;
const HEADER_HEIGHT = 50;
const HORIZONTAL_SCROLL_HEIGHT = 8;

interface MutationTask<T> {
  call$: () => Observable<GridMutationResult<T>>;
  onError?: () => void;
  onSettled?: () => void;
  onSuccess?: () => void;
}

@Component({
  imports: [AgGridModule, EditabledCtaRowComponent],
  selector: 'cam-ag-grid-editabled',
  styleUrl: './ag-grid-editabled.component.scss',
  templateUrl: './ag-grid-editabled.component.html',
})
export class AgGridEditabledComponent<T extends EditabledGridRow> extends BaseGridComponent<T> implements OnInit {
  services = input<IEditabledDataService<T> | null>(null);

  colsMetaData = input<GridMetaData[] | null>(null);

  allowStructureEdition = input(true);

  /** Types de lignes proposes, dans la barre d'ajout comme dans le menu contextuel. */
  allowedRowTypes = input<RowType[]>(ALL_ROW_TYPES);

  private _config: IEditabledConfig = inject(EDITABLED_CONFIG, { optional: true }) ?? DEFAULT_EDITABLED_CONFIG;
  private _notificationService = inject(LAZY_SERVICE_TOKEN);

  readonly getRowId: GetRowIdFunc<T> = params => params.data.id;

  // Slightly tighter rows than the read-only grid (40px) — denser editing layout.
  override readonly rowHeight = 38;

  readonly rowModelType: RowModelType = 'serverSide';

  readonly rowCount = signal(0);
  readonly displayedRowCount = signal(0);
  readonly isEmpty = computed(() => this.rowCount() === 0);
  readonly domLayout = computed<DomLayoutType>(() => this.gridOptions().domLayout ?? 'normal');
  readonly gridHeight = computed(() =>
    this.domLayout() === 'normal'
      ? HEADER_HEIGHT + HORIZONTAL_SCROLL_HEIGHT + this.displayedRowCount() * this.rowHeight
      : null
  );

  private _pendingCalls = 0;
  private _structurePending = false;
  private readonly _pendingEdits = new Map<string, IEditabledDataUpdateFields<T>>();
  private readonly _previousValues = new Map<string, IEditabledDataUpdateFields<T>>();
  // Every backend call goes through this queue so responses are applied in the order actions were made.
  private readonly _mutations$ = new Subject<MutationTask<T>>();
  private readonly _rowSelectedForDuplicate = signal<string | null>(null);
  private readonly _rowSelectedForMove = signal<string | null>(null);

  // Source unique du menu contextuel et des raccourcis clavier.
  private readonly _menu = computed<ContentMenuAction[]>(() => {
    if (!this.allowStructureEdition()) {
      return [];
    }
    const context: EditabledMenuContext = {
      allowedRowTypes: this.allowedRowTypes(),
      rowSelectedForDuplicate: () => this._rowSelectedForDuplicate(),
      rowSelectedForMove: () => this._rowSelectedForMove(),
      services: this.services(),
    };
    return this.contextMenuItems().length > 0
      ? [...this.contextMenuItems(), ...buildSelectionMenuItems(context)]
      : createEditabledContextMenu(context);
  });

  constructor() {
    super();
    CamTranslationGrid.getInstance();
  }

  override ngOnInit() {
    super.ngOnInit();
    this._grid.switchView('grid', false);
  }

  override ngOnDestroy() {
    // la file meurt avec le composant : les editions encore en attente partent hors file
    const batch = this._takePendingEdits();
    if (batch) {
      this.services()
        ?.updateField$(batch.changes)
        .subscribe({ error: error => Logger.LogError('[GRID-EDITABLE] pending edits failed on destroy', error) });
    }
    super.ngOnDestroy();
    this._grid.destroy();
  }

  public isReady(event: GridReadyEvent) {
    super.onGridReady(event as unknown as PGridReadyEvent);

    this._registerSubscription(this._mutations$.pipe(concatMap(task => this._run$(task))).subscribe());

    this._registerSubscription(
      this._grid.editabledData$
        .pipe(
          tap(edit => this._bufferEdit(edit)),
          debounceTime(this._config.debounceTimeMs)
        )
        .subscribe(() => this._flushEdits())
    );

    this._registerSubscription(this._grid.dataUpdated$.subscribe(() => this._refreshRowCount()));

    this._registerSubscription(this._grid.shortcutAction$.subscribe(event => this.onShortcut(event)));

    this._registerSubscription(
      this._grid.contextMenuAction$.subscribe(({ action, item, position, targetType }) =>
        this._dispatch(action, item?.id, position, targetType)
      )
    );

    const services = this.services();
    if (!services) {
      return;
    }
    this._registerSubscription(
      this._load$(services.getData$(this._grid.showHidden)).subscribe(items => this._setupGridData(items))
    );
    this._registerSubscription(
      this._grid.showHiddenChanged$
        .pipe(switchMap(showHidden => this._load$(services.getData$(showHidden))))
        .subscribe(items => {
          this._grid.updateData(items);
          this._refreshRowCount();
        })
    );
  }

  public addLine(type: RowType, relatedId?: string, position?: InsertPosition) {
    const services = this.services();
    if (services?.addLine$) {
      this._mutate(() => services.addLine$?.(type, relatedId, position) ?? EMPTY);
    }
  }

  public convertLine(id: string, targetType: RowType) {
    const services = this.services();
    if (services?.convertLine$) {
      this._mutate(() => services.convertLine$?.(id, targetType) ?? EMPTY);
    }
  }

  public deleteLine(id: string) {
    const services = this.services();
    if (services?.deleteLine$) {
      this._mutate(() => services.deleteLine$?.(id) ?? EMPTY);
    }
  }

  public hideLine(id: string) {
    const services = this.services();
    if (services?.hideLine$) {
      this._mutate(() => services.hideLine$?.(id) ?? EMPTY);
    }
  }

  public selectForMove(id: string) {
    this._rowSelectedForDuplicate.set(null);
    this._rowSelectedForMove.set(id);
    this._refreshDescriptionCells();
  }

  public moveTo(targetId: string, position: InsertPosition) {
    const sourceId = this._rowSelectedForMove();
    const services = this.services();
    if (!sourceId || sourceId === targetId || !services?.moveLine$) {
      return;
    }
    this._mutate(
      () => services.moveLine$?.(sourceId, targetId, position) ?? EMPTY,
      () => this._clearSelection(this._rowSelectedForMove)
    );
  }

  public selectForDuplicate(id: string) {
    this._rowSelectedForMove.set(null);
    this._rowSelectedForDuplicate.set(id);
    this._refreshDescriptionCells();
  }

  public duplicateTo(targetId: string, position: InsertPosition) {
    const sourceId = this._rowSelectedForDuplicate();
    const services = this.services();
    // contrairement au deplacement, dupliquer une ligne juste au-dessus ou en dessous d'elle-meme est un usage courant
    if (!sourceId || !services?.duplicateLineTo$) {
      return;
    }
    this._mutate(
      () => services.duplicateLineTo$?.(sourceId, targetId, position) ?? EMPTY,
      () => this._clearSelection(this._rowSelectedForDuplicate)
    );
  }

  public onShortcut({ id, position, shortcut }: EditabledShortcutEvent) {
    this._dispatch(shortcut.action, id, position, shortcut.targetType);
  }

  public onGridLayoutChanged(hostWidth?: number) {
    this._resizePinnedFlexColumns(hostWidth);
  }

  public onModelUpdated(event: ModelUpdatedEvent<T>) {
    this.displayedRowCount.set(event.api.getDisplayedRowCount());
  }

  // Context menu and keyboard shortcuts resolve to the same actions.
  private _dispatch(
    action: ContentMenuActionType | 'openInExternalLink' | 'separator',
    id: string | undefined,
    position?: InsertPosition,
    targetType?: RowType
  ) {
    const added = ADD_ROW_ACTIONS.find(entry => entry.action === action);
    if (added) {
      this.addLine(added.type, id, position);
      return;
    }
    if (!id) {
      return;
    }
    switch (action) {
      case 'convertLine':
        if (targetType !== undefined) {
          this.convertLine(id, targetType);
        }
        break;
      case 'deleteLine':
        this.deleteLine(id);
        break;
      case 'duplicateTo':
        if (position !== undefined) {
          this.duplicateTo(id, position);
        }
        break;
      case 'hideLine':
        this.hideLine(id);
        break;
      case 'moveTo':
        if (position !== undefined) {
          this.moveTo(id, position);
        }
        break;
      case 'selectForDuplicate':
        this.selectForDuplicate(id);
        break;
      case 'selectForMove':
        this.selectForMove(id);
        break;
    }
  }

  // One structure action at a time (no double click), always after the edits typed before it.
  private _mutate(call$: () => Observable<GridMutationResult<T>>, onSuccess?: () => void) {
    if (this._structurePending) {
      return;
    }
    this._structurePending = true;
    this._flushEdits();
    this._mutations$.next({ call$, onSettled: () => (this._structurePending = false), onSuccess });
  }

  private _run$(task: MutationTask<T>): Observable<unknown> {
    return defer(task.call$).pipe(
      this._loading(),
      tap(result => {
        Logger.LogDebug('[GRID-EDITABLE][AgGridEditabledComponent] mutation result → applying to grid', result);
        this._grid.updateRows(result);
        this._clearStaleSelections();
        task.onSuccess?.();
      }),
      catchError(error => {
        task.onError?.();
        this._onError(error);
        return EMPTY;
      }),
      finalize(() => task.onSettled?.())
    );
  }

  private _bufferEdit({ changes, previous }: EditabledCellEdit<T>) {
    this._pendingEdits.set(changes.id, { ...this._pendingEdits.get(changes.id), ...changes });
    // keep the value from before the first unconfirmed edit of each field
    this._previousValues.set(changes.id, { ...previous, ...this._previousValues.get(changes.id) });
  }

  private _takePendingEdits() {
    if (this._pendingEdits.size === 0) {
      return null;
    }
    const batch = { changes: [...this._pendingEdits.values()], previous: [...this._previousValues.values()] };
    this._pendingEdits.clear();
    this._previousValues.clear();
    return batch;
  }

  private _flushEdits() {
    const services = this.services();
    const batch = this._takePendingEdits();
    if (!batch || !services) {
      return;
    }
    Logger.LogDebug('[GRID-EDITABLE][AgGridEditabledComponent] cell edits batched → sending to backend', batch.changes);
    this._mutations$.next({
      call$: () => services.updateField$(batch.changes),
      onError: () => this._restore(batch.previous),
    });
  }

  // A failed save puts the typed cells back to what the backend still holds.
  private _restore(previous: IEditabledDataUpdateFields<T>[]) {
    this._grid.updateRows({
      update: previous.map(row => ({
        modifiedProperties: Object.keys(row).filter(key => key !== 'id'),
        row: row as T,
      })),
    });
  }

  private _onError(error: unknown) {
    Logger.LogError('[GRID-EDITABLE][AgGridEditabledComponent] backend call failed', error);
    this._notificationService.addNotification('notification.common.error', ENotificationCode.error);
  }

  private _clearSelection(selected: WritableSignal<string | null>) {
    selected.set(null);
    this._refreshDescriptionCells();
  }

  // A selected move/duplicate source that was removed or hidden can no longer be placed.
  private _clearStaleSelections() {
    const selections = [this._rowSelectedForMove, this._rowSelectedForDuplicate].filter(selected => !!selected());
    if (selections.length === 0) {
      return;
    }
    const ids = new Set(this._grid.dataSource.getData().map(row => row.id));
    selections.filter(selected => !ids.has(selected()!)).forEach(selected => this._clearSelection(selected));
  }

  private _refreshDescriptionCells() {
    this._grid.api?.refreshCells({ columns: ['description'], force: true });
  }

  private _resizePinnedFlexColumns(hostWidth?: number) {
    const api = this._grid.api;
    if (!api) {
      return;
    }

    const flexColumns = api.getAllDisplayedColumns().filter(column => !!column.getPinned() && !!column.getFlex());
    if (flexColumns.length === 0) {
      return;
    }

    const { left, right } = api.getHorizontalPixelRange();
    const centerViewportWidth = right - left;
    if (hostWidth === undefined && centerViewportWidth <= 0) {
      return;
    }

    const pinnedColumns = [...api.getDisplayedLeftColumns(), ...api.getDisplayedRightColumns()];
    const bodyWidth = hostWidth ?? centerViewportWidth + this._sumWidths(pinnedColumns);
    if (bodyWidth <= PINNED_VIEWPORT_MARGIN) {
      return;
    }

    const otherWidth = this._sumWidths(api.getAllDisplayedColumns().filter(column => !flexColumns.includes(column)));
    const otherPinnedWidth = this._sumWidths(pinnedColumns.filter(column => !flexColumns.includes(column)));
    const available = Math.min(bodyWidth - otherWidth, bodyWidth - PINNED_VIEWPORT_MARGIN - otherPinnedWidth);
    const totalFlex = flexColumns.reduce((total, column) => total + (column.getFlex() ?? 0), 0);

    const widths = flexColumns.map(column => ({
      key: column,
      newWidth: Math.max(
        column.getColDef().width ?? DEFAULT_COLUMN_WIDTH,
        Math.round((available * (column.getFlex() ?? 0)) / totalFlex)
      ),
    }));
    if (widths.every(({ key, newWidth }) => Math.abs(key.getActualWidth() - newWidth) < 1)) {
      return;
    }

    api.setColumnWidths(widths, true, 'flex');
  }

  private _sumWidths(columns: Column[]) {
    return columns.reduce((total, column) => total + column.getActualWidth(), 0);
  }

  private _refreshRowCount() {
    this.rowCount.set(this._grid.dataSource.getData().length);
  }

  private _setupGridData(items: T[]) {
    this.rowCount.set(items.length);

    const menu = this._menu();
    this._grid.shortcutMenu = menu.filter((item): item is IContentMenuActionConfig => typeof item !== 'string');

    const descriptionOverride = this.fieldsOverrides()?.['description'];
    const fieldsOverrides = {
      ...(this.fieldsOverrides() ?? {}),
      description: {
        flex: 1,
        ...(descriptionOverride ?? {}),
        cellRendererSelector: (params: { data?: T }) => {
          if (params.data?.rowType === RowType.Comment) {
            return { component: GridCommentRowComponent };
          }
          if (params.data?.id && params.data.id === this._rowSelectedForMove()) {
            return { component: EditabledSelectedRowComponent, params: { selectedIcon: 'clockwise-2-line' } };
          }
          if (params.data?.id && params.data.id === this._rowSelectedForDuplicate()) {
            return { component: EditabledSelectedRowComponent, params: { selectedIcon: 'file-copy-2-line' } };
          }
          return undefined;
        },
      },
    };

    const services = this.services();
    this._grid.setData({
      colsMetaData: this.colsMetaData(),
      data: items,
      fieldsOverrides,
      options: {
        ...this.gridOptions(),
        colOptions: {
          ...this.colOptions(),
          dataSearch$: services?.getFieldsData$?.bind(services),
          editable: (params: { data?: T; colDef?: { field?: string } }) => {
            const field = params.colDef?.field;
            if (params.data?.rowType === RowType.Chapter || params.data?.rowType === RowType.Comment) {
              return field === 'description';
            }
            return true;
          },
          sortable: false,
        },
        contextMenuItems: menu,
        openByDefault: this.openByDefault(),
        treeData: this.treeData(),
      },
      showHierarchyLabel: false,
    });
  }

  private _load$(data$: Observable<{ items: T[] }>): Observable<T[]> {
    return data$.pipe(
      map(data => data.items),
      this._loading(),
      catchError(error => {
        this._onError(error);
        return EMPTY;
      })
    );
  }

  private _loading<R>(): MonoTypeOperatorFunction<R> {
    return source =>
      source.pipe(
        tap({ subscribe: () => this._startLoading() }),
        finalize(() => this._stopLoading())
      );
  }

  private _startLoading() {
    this._pendingCalls++;
    this.requestState.asked();
  }

  private _stopLoading() {
    this._pendingCalls = Math.max(0, this._pendingCalls - 1);
    if (this._pendingCalls === 0) {
      this.requestState.completed();
    }
  }
}
